// components/GetInTouchSection.jsx
import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import styled from 'styled-components';
import { Container } from "../styles/StyledComponents";

const Section = styled.section`
  padding: 100px 2rem;
  background: #000000;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 100vw;
    height: 1px;
    background: #333333;
  }

  &::before { top: 0; }
  &::after { bottom: 0; }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Square = styled.div`
  width: 15px;
  height: 15px;
  background: #ffffff;
  flex-shrink: 0;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  color: #ffffff;
  margin: 0;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 4rem;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const FormTitle = styled.h3`
  font-size: 3rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 1024px) {
    font-size: 2.8rem;
    text-align: left;
  }

  @media (max-width: 640px) {
    font-size: 2.4rem;
  }
`;

const ContactInfo = styled.div`
  color: #ffffff;
  font-size: 1.1rem;
  line-height: 1.8;

  a {
    color: #ffffff;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.3s ease;

    &:hover {
      border-bottom-color: #ffffff;
    }
  }

  div {
    margin-bottom: 15px;
  }

  @media (max-width: 1024px) {
    text-align: left;
  }
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 0;
  border-radius: 0;
  font-size: 1.1rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid #ddddddff;
  color: #ffffff;
  transition: border-color 0.3s ease;

  &::placeholder { color: #bcbcbcff; }
  &:focus {
    outline: none;
    border-bottom-color: #ffffff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 0;
  font-size: 1.1rem;
  border-radius: 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid #ddddddff;
  color: #ffffff;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.3s ease;

  &::placeholder { color: #bcbcbcff; }
  &:focus {
    outline: none;
    border-bottom-color: #ffffff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1rem 0;
  font-size: 1.1rem;
  background: transparent;
  border: none;
  border-radius: 0;
  border-bottom: 1px solid #ddddddff;
  color: #ffffff;
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23dddddd' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right center;
  background-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-bottom-color: #ffffff;
  }

  option {
    color: #000;
    background: #fff;
  }
`;

const SubmitButton = styled.button`
  margin-top: 2rem;
  padding: 1rem 2.8rem;
  font-size: 1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: transparent;
  color: #ffffff;
  border: 1px solid #ffffff;
  cursor: pointer;
  border-radius: 0;
  transition: all 0.4s ease;
  align-self: flex-start;

  &:hover {
    background: #ffffff;
    color: #000000;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    width: 100%;
    text-align: center;
    align-self: stretch;
  }
`;

const StatusMessage = styled.p`
  margin-top: 1rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${props => (props.success ? '#18DE39' : '#ff3333')};
`;

const GetInTouchSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    intent: '',        // This was missing before — now fixed
    service: '',
    subject: '',
  });

  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const userId = process.env.REACT_APP_EMAILJS_USER_ID;

    emailjs.send(serviceId, templateId, formData, userId)
      .then(() => {
        setStatus('Thank you! Your message has been sent successfully.');
        setFormData({ name: '', email: '', intent: '', service: '', subject: '' });
      })
      .catch((error) => {
        console.error('EmailJS Error:', error);
        setStatus('Failed to send message. Please try again later.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Section id="get-in-touch">
      <Container>
        <TitleRow>
          <Square />
          <SectionTitle>Contact Us</SectionTitle>
        </TitleRow>

        <Grid>
          <LeftColumn>
            <FormTitle>
              Ready to build your future team?
              Ready to step into your new career?
            </FormTitle>

            <ContactInfo>
              <div>
                <strong>Email</strong><br />
                {/* <a href="mailto:lizzy@startsomethingdifferentglobal.com">
                  info@startsomethingdifferentglobal.com
                </a><br /> */}
                info@startsomethingdifferentglobal.com
              </div>
              <div>
                <strong>Phone</strong><br />
                {/* <a href="mailto:lizzy@startsomethingdifferentglobal.com">
                  info@startsomethingdifferentglobal.com
                </a><br /> */}
                +44 (0) 208 191 3394
              </div>
            </ContactInfo>
          </LeftColumn>

          <FormColumn>
            <form onSubmit={handleSubmit}>
              <Input type="text" name="name" placeholder="Name" value={formData.name} onChange={handleChange} required />
              <Input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleChange} required />

              {/* YOUR ORIGINAL RADIO BUTTONS — 100% UNCHANGED VISUALLY */}
              <div style={{ margin: '1rem 0' }}>
                <div style={{ 
                  color: '#fff', 
                  fontSize: '1.1rem', 
                  marginBottom: '1rem',
                  fontWeight: '500'
                }}>
                  I am…
                </div>

                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.9rem', 
                  color: '#fff', 
                  fontSize: '1.1rem',
                  marginBottom: '1.2rem',
                  cursor: 'pointer'
                }}>
                  <input 
                    type="radio" 
                    name="intent" 
                    value="hiring" 
                    onChange={handleChange} 
                    checked={formData.intent === 'hiring'}
                    required 
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  Hiring / Looking for talent
                </label>

                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.9rem', 
                  color: '#fff', 
                  fontSize: '1.1rem',
                  cursor: 'pointer'
                }}>
                  <input 
                    type="radio" 
                    name="intent" 
                    value="jobseeker" 
                    onChange={handleChange} 
                    checked={formData.intent === 'jobseeker'}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  Looking to get hired
                </label>
              </div>

              <Select name="service" value={formData.service} onChange={handleChange} required>
                <option value="" disabled>Select a service</option>
                <option value="Recruitment Services">Recruitment Services</option>
                <option value="Temporary, Freelance, Contract">Temporary, Freelance, Contract</option>
                <option value="Permanent">Permanent</option>
                <option value="Executive Search">Executive Search</option>
                <option value="Talent Partnering and Consulting">Talent Partnering and Consulting</option>
                <option value="CV Sessions">CV Sessions</option>
                <option value="Workshops">Workshops</option>
                <option value="Other">Other</option>
              </Select>

              <TextArea name="subject" placeholder="Subject / Message" value={formData.subject} onChange={handleChange} required />

              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Submit'}
              </SubmitButton>

              {status && (
                <StatusMessage success={status.includes('Thank you')}>
                  {status}
                </StatusMessage>
              )}
            </form>
          </FormColumn>
        </Grid>
      </Container>
    </Section>
  );
};

export default GetInTouchSection;